<script setup>
  import { ref, inject, onMounted, computed } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import DataTable from '@/components/DataTable.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import CurrencyInput from '@/components/CurrencyInput.vue'
  import { useCompanyStore } from '@/stores/companyStore'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useRegionStore } from '@/stores/regionStore'
  import { useContractStore } from '@/stores/contractStore'
  import { industries } from '@/components/logics/Consts'
  import { validateCorporateNumber, convertToHalfWidthNumber, convertToFullWidth } from '@/components/logics/Util'

  const router = useRouter()
  const route = useRoute()

  const companyStore = useCompanyStore()
  const dialogStore = useDialogStore()
  const regionStore = useRegionStore()
  const contractStore = useContractStore()

  // パンくずリスト
  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: '会社一覧' }]

  // data ///////////////////////////////////////////
  const searchText = ref('')
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')

  // 編集用データ
  const showPopupEdit = ref(false)
  const isEdit = ref(false)

  // computed ///////////////////////////////////////

  // 新規作成ボタン表示
  const showNewButton = computed(() => {
    return true
  })

  // 入力ハンドラ /////////////////////////////////////////
  // 法人番号入力時の処理
  const handleCorporateNumberInput = event => {
    // validateCorporateNumberの戻り値を使って更新
    companyStore.formData.corporateNumber = validateCorporateNumber(event)
  }

  // 全角文字入力の処理
  const handleFullWidthInput = (event, key) => {
    companyStore.formData[key] = convertToFullWidth(event.target.value)
  }

  // 電話番号入力時の処理
  const handlePhoneInput = (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    companyStore.formData.phone[index] = halfWidthValue
  }

  // FAX番号入力時の処理
  const handleFaxInput = (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    companyStore.formData.fax[index] = halfWidthValue
  }

  // 郵便番号入力時の処理
  const handlePostalCodeInput = async (event, index) => {
    // 全角を半角に変換
    const value = event.target.value
    const halfWidthValue = convertToHalfWidthNumber(value)
    companyStore.formData.postalCode[index] = halfWidthValue

    // 両方の入力が揃っているか確認
    if (companyStore.isPostalCodeFilled()) {
      companyStore.setAddress(await regionStore.getAddressWithPostalCode(companyStore.postalCodeWithHyphen))
    }
  }

  // UI ////////////////////////////////////////

  // 都道府県選択時の処理
  const onPrefectureSelected = event => {
    // 都道府県名からカナを取得して設定
    joinStore.formData.prefectureKana = getKanaFromPrefecture(event.target.value)
  }

  // methods ////////////////////////////////////////
  onMounted(async () => {
    // データ取得
    // await contractStore.checkReadOnlyStatus()
    await regionStore.getPrefectures()
    await companyStore.getCompanies()
    companyStore.initTableList()

    // クエリのactionがnewの場合、新規登録ダイアログを開く
    if (route.query.action === 'new') {
      openEditCompany()
    }
  })

  // 会社編集ダイアログを開く
  const openEditCompany = async item => {
    isEdit.value = !!item
    await companyStore.setFormData(item?.id)

    showPopupEdit.value = true
  }

  // ユーザー保存
  const onSave = async () => {
    const message = await companyStore.saveCompany()
    if (message) {
      showPopupEdit.value = false
      await dialogStore.showMessageDialog('確認', message)
    }
  }

  // ユーザー削除
  const onDelete = async () => {
    if (await dialogStore.showConfirmDialog('確認', '本当に削除しますか？削除すると、この会社に紐づく事業場と従業員もすべて削除されます')) {
      const message = await companyStore.deleteCompany()
      if (message) {
        showPopupEdit.value = false
        await dialogStore.showMessageDialog('確認', message)
      }
    }
  }

  // ユーザー複製
  const onCopy = async () => {
    companyStore.copyCompany()
    isEdit.value = false
  }

  // 行のアクション
  const handleRowAction = ({ table, item }) => {
    // 選択された会社の名前を設定
    companyStore.currentCompanyName = item.name
    // 事業場一覧へ遷移
    if (table.key === 'branch') {
      router.push({ name: 'branch', params: { companyId: item.id } })
    }
    // 従業員一覧へ遷移
    else if (table.key === 'staff') {
      router.push({ name: 'staff', params: { companyId: item.id } })
    }
    // ストレージへ遷移（同一タブで開く）
    else if (table.key === 'storage') {
      ;(async () => {
        if (!(await contractStore.getIsActive())) {
          dialogStore.showMessageDialog('エラー', '有料プランの契約が必要です')
          return
        }
        // uuidがない場合はエラーを表示
        if (!item.uuid) {
          dialogStore.showMessageDialog('エラー', 'ストレージを開けません')
          return
        }
        router.push({ name: 'storage', params: { uuid: item.uuid } })
      })()
    }
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <h2>会社一覧</h2>
      <button v-if="showNewButton" type="button" class="new" @click="openEditCompany()">新規登録</button>

      <div class="filters">
        <!-- 検索ボックス -->
        <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" v-model="searchText" placeholder="検索" />
        </div>

        <div class="search-result">
          {{ length }}
        </div>
      </div>
    </div>

    <div class="main-body">
      <DataTable :table-list="companyStore.tableList" :data-list="companyStore.companies" :search-text="searchText" :sort-key="sortKey" :sort-desc="sortDesc" @select="openEditCompany" @length="length = $event" @row-action="handleRowAction"></DataTable>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <!-- 編集ダイアログ -->
  <EditDialog v-model:visible="showPopupEdit" :show-save-button="true" :show-delete-button="isEdit" :show-copy-button="isEdit" :save-button-text="isEdit ? '保存' : '登録'" @save="onSave" @copy="onCopy" @delete="onDelete">
    <template v-slot:title>
      {{ isEdit ? '編集' : '新規登録' }}
    </template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt>会社名</dt>
          <dd>
            <input id="company-name" type="text" v-model="companyStore.formData.name" v-autokana:company-name @input="handleFullWidthInput($event, 'name')" placeholder="全角で入力してください" />
          </dd>
          <dt>会社名カナ</dt>
          <dd>
            <input id="company-name-kana" type="text" v-model="companyStore.formData.nameKana" @input="handleFullWidthInput($event, 'nameKana')" placeholder="全角カタカナで入力してください" />
          </dd>
          <dt>法人番号</dt>
          <dd>
            <input type="text" v-model="companyStore.formData.corporateNumber" maxlength="13" @input="handleCorporateNumberInput" placeholder="1234567890123" />
          </dd>
          <div v-if="isEdit">
            <dt v-if="isEdit">従業員数</dt>
            <dd v-if="isEdit">
              <input type="text" v-model="companyStore.formData.employeeCount" disabled />
            </dd>
            <!-- <dt v-if="isEdit">役員数</dt>
            <dd v-if="isEdit">
              <input type="text" v-model="companyStore.formData.officerCount" disabled />
            </dd> -->
          </div>
          <dt>郵便番号</dt>
          <dd>
            <div class="postal-code-grid">
              <input type="text" v-model="companyStore.formData.postalCode[0]" placeholder="123" maxlength="3" @input="handlePostalCodeInput($event, 0)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="companyStore.formData.postalCode[1]" placeholder="4567" maxlength="4" @input="handlePostalCodeInput($event, 1)" />
            </div>
          </dd>
          <dt>都道府県</dt>
          <dd>
            <div class="select">
              <select v-model="companyStore.formData.prefecture">
                <option value="">選択してください</option>
                <option v-for="item in regionStore.prefectureNames" :key="item" :value="item">{{ item }}</option>
              </select>
            </div>
          </dd>
          <dt>住所</dt>
          <dd>
            <input id="address" type="text" v-model="companyStore.formData.address" v-autokana:address @input="handleFullWidthInput($event, 'address')" placeholder="全角で入力してください" />
          </dd>
          <dt>住所カナ</dt>
          <dd>
            <input id="address-kana" type="text" v-model="companyStore.formData.addressKana" @input="handleFullWidthInput($event, 'addressKana')" placeholder="全角カタカナで入力してください" />
          </dd>
          <dt v-if="!isEdit">業種</dt>
          <dd v-if="!isEdit">
            <input type="text" v-model="companyStore.formData.industry" @input="handleFullWidthInput($event, 'industry')" placeholder="全角で入力してください" />
            <div class="select mini">
              <select @change="companyStore.formData.industry = $event.target.value">
                <option v-for="industry in industries" :key="industry" :value="industry">{{ industry }}</option>
              </select>
            </div>
          </dd>

          <dt>電話番号</dt>
          <dd>
            <div class="phone-grid">
              <input type="text" v-model="companyStore.formData.phone[0]" placeholder="012" maxlength="5" @input="handlePhoneInput($event, 0)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="companyStore.formData.phone[1]" placeholder="3456" maxlength="4" @input="handlePhoneInput($event, 1)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="companyStore.formData.phone[2]" placeholder="7890" maxlength="4" @input="handlePhoneInput($event, 2)" />
            </div>
          </dd>
          <dt>FAX番号</dt>
          <dd>
            <div class="phone-grid">
              <input type="text" v-model="companyStore.formData.fax[0]" placeholder="012" maxlength="5" @input="handleFaxInput($event, 0)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="companyStore.formData.fax[1]" placeholder="3456" maxlength="4" @input="handleFaxInput($event, 1)" />
              <span class="hyphen">-</span>
              <input type="text" v-model="companyStore.formData.fax[2]" placeholder="7890" maxlength="4" @input="handleFaxInput($event, 2)" />
            </div>
          </dd>
          <dt>代表者役職</dt>
          <dd>
            <input id="representative-title" type="text" v-model="companyStore.formData.representativeTitle" v-autokana:representative-title @input="handleFullWidthInput($event, 'representativeTitle')" />
          </dd>
          <dt>代表者役職カナ</dt>
          <dd>
            <input id="representative-title-kana" type="text" v-model="companyStore.formData.representativeTitleKana" @input="handleFullWidthInput($event, 'representativeTitleKana')" />
          </dd>
          <dt>代表者氏名</dt>
          <dd>
            <div class="name-grid">
              <input id="representative-last" type="text" v-model="companyStore.formData.representativeLastName" v-autokana:representative-last @input="handleFullWidthInput($event, 'representativeLastName')" placeholder="姓" />
              <input id="representative-first" type="text" v-model="companyStore.formData.representativeFirstName" v-autokana:representative-first @input="handleFullWidthInput($event, 'representativeFirstName')" placeholder="名" />
            </div>
          </dd>
          <dt>代表者氏名カナ</dt>
          <dd>
            <div class="name-grid">
              <input id="representative-last-kana" type="text" v-model="companyStore.formData.representativeLastKana" @input="handleFullWidthInput($event, 'representativeLastKana')" placeholder="セイ" />
              <input id="representative-first-kana" type="text" v-model="companyStore.formData.representativeFirstKana" @input="handleFullWidthInput($event, 'representativeFirstKana')" placeholder="メイ" />
            </div>
          </dd>
          <dt>健康保険組合名</dt>
          <dd>
            <input type="text" v-model="companyStore.formData.healthInsurance" />
          </dd>
          <dt>メモ</dt>
          <dd>
            <textarea v-model="companyStore.formData.memo"></textarea>
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>
</template>

<style lang="less" scoped></style>
